
import {
  computed,
  ComputedRef,
  defineComponent,
  reactive,
  Ref,
  ref,
  watch,
  onMounted,
} from 'vue';

import { useStore } from '@/store';

import { ElementPartTypeEnum, TemplateStructureEnum } from '@/types/enums';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import InputSwitch from 'primevue/inputswitch';
import Button from 'primevue/button';
import InlineMessage from 'primevue/inlinemessage';
import Tooltip from 'primevue/tooltip';
import { useI18n } from 'vue-i18n';
import { required } from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';
import FieldErrors from '@/components/fields/partials/FieldErrors.vue';
import { showToastSuccess } from '@/helpers';
import { isTemplateRelatedToCampaignBulk, getTemplatePartsById } from '@/composables/template-editor/TemplateEditor';
import { cleanupDynamicStyle } from '@/components/template-builder/utils/parser';
import { TEMPLATE_PART_ELEMENT_PART_TYPE, TEMPLATE_SYNC_ELEMENT_IDENTIFIER } from '@/components/template-builder/utils/constants';

export default defineComponent({
  name: 'SaveElementModal',

  components: {
    Dialog,
    InputText,
    InputSwitch,
    Button,
    InlineMessage,
    FieldErrors,
  },

  directives: {
    tooltip: Tooltip,
  },

  props: {
    position: {
      type: String,
      required: false,
      default: 'center',
    },

    displayDialog: {
      type: Boolean,
      required: true,
    },

    displayCancelButton: {
      type: Boolean,
      required: false,
      default: false,
    },

    closeOnValidate: {
      type: Boolean,
      required: false,
      default: true,
    },

    cancelButtonText: {
      type: String,
      required: false,
      default: 'templateBuilder.modals.cancel',
    },

    validateButtonText: {
      type: String,
      required: false,
      default: 'templateBuilder.modals.validate',
    },

    elementName: {
      type: String,
      required: true,
    },

    elementSync: {
      type: Boolean,
      required: false,
      default: false,
    },

    elementType: {
      type: String,
      required: false,
      default: TemplateStructureEnum.SECTION,
    },

    showLoading: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  emits: ['on-close-dialog', 'on-search-elements', 'on-validate'],

  setup(props, context) {
    const { t } = useI18n();
    const store = useStore();
    const displayModal: ComputedRef<boolean> = computed(() => props.displayDialog);

    const forceSync = computed(() => store.state.liveEditor.saveElementModalPayload.forceSync);
    const renameElement = computed(() => store.state.liveEditor.saveElementModalPayload.renameElement);
    const syncParentSyncId = computed(() => store.state.liveEditor.saveElementModalPayload.syncParentSyncId);
    const syncParentId = computed(() => store.state.liveEditor.saveElementModalPayload.syncParentId);
    const elementPartType = computed(() => store.state.liveEditor.saveElementModalPayload.elementPartType);
    const fromSyncElement = computed(() => store.state.liveEditor.saveElementModalPayload.fromSyncElement);

    const headerTitle: ComputedRef<string> = computed(() => {
      if (props.elementType === TemplateStructureEnum.DESIGN) {
        return 'templateBuilder.modals.giveNameToDesign';
      }
      if (elementPartType.value === ElementPartTypeEnum.STYLES) {
        return 'templateBuilder.modals.giveNameToStyles';
      }
      return 'templateBuilder.modals.giveNameToElement';
    });

    const inputIsSynchronizedDescription: ComputedRef<string> = computed(() => {
      if (props.elementType === TemplateStructureEnum.DESIGN) {
        return 'templateBuilder.modals.saveAsSyncedDesign';
      }
      if (elementPartType.value === ElementPartTypeEnum.STYLES) {
        return 'templateBuilder.modals.saveAsSyncedStyles';
      }
      return 'templateBuilder.modals.saveAsSyncedElement';
    });

    const templateRelatedToCampaignBulk = ref(isTemplateRelatedToCampaignBulk());

    // Determine if we can save style only (for sections and widgets)
    const canSaveStyleOnly: ComputedRef<boolean> = computed(
      () => !elementPartType.value && (props.elementType === TemplateStructureEnum.SECTION || props.elementType === TemplateStructureEnum.WIDGET),
    );

    const name: Ref<string> = ref(props.elementName);
    const syncState: Ref<boolean> = ref(props.elementSync);
    const isElementSelectedFromSearch: Ref<boolean> = ref(false);
    const selectedElementId: Ref<number | null> = ref(null);
    const originalElementName: Ref<string> = ref(props.elementName);
    const originalSyncState: Ref<boolean> = ref(props.elementSync);
    const saveStyleOnly: Ref<boolean> = ref(false);

    const loading = ref(false);

    const state = reactive({
      name,
      syncState,
    });

    const rules = {
      name: { required },
      syncState: {},
    };

    const v$ = useVuelidate(rules, state);

    watch(() => props.elementName, (newValue, oldValue) => {
      if (newValue !== oldValue) {
        name.value = newValue;
        // Reset selection state when elementName is cleared from outside
        if (newValue === '') {
          isElementSelectedFromSearch.value = false;
          originalElementName.value = '';
        } else {
          originalElementName.value = newValue;
        }
      }
    });

    watch(() => props.elementSync, (newValue, oldValue) => {
      if (newValue !== oldValue) {
        syncState.value = newValue;
      }
    });

    // Watch for modal visibility to reset state when it opens
    watch(() => props.displayDialog, (newValue, oldValue) => {
      if (newValue && !oldValue) {
        // Modal is opening, reset the selection state if elementName is empty
        if (!props.elementName || props.elementName === '') {
          isElementSelectedFromSearch.value = false;
          originalElementName.value = '';
        }
      }
    });

    const handleClose = () => {
      context.emit('on-close-dialog');
      name.value = props.elementName;
      syncState.value = props.elementSync;
      isElementSelectedFromSearch.value = false;
      originalElementName.value = props.elementName;
    };

    const handleSearchElements = () => {
      context.emit('on-search-elements', {
        elementType: props.elementType,
        elementPartType: saveStyleOnly.value ? ElementPartTypeEnum.STYLES : elementPartType.value,
        elementSubtype: '', // Could be extended later if needed
        includeAllElementsPartType: canSaveStyleOnly.value,
      });
    };

    const handleClearSelection = () => {
      // Clear the selection and restore original name and sync state
      isElementSelectedFromSearch.value = false;
      selectedElementId.value = null;
      name.value = originalElementName.value;
      syncState.value = originalSyncState.value;
    };

    const markAsSelectedFromSearch = async (elementId?: number) => {
      // Mark that the current name was selected from search
      isElementSelectedFromSearch.value = true;
      originalElementName.value = props.elementName; // Save the original name
      originalSyncState.value = syncState.value; // Save the original sync state

      if (elementId) {
        selectedElementId.value = elementId;

        // Get the selected element data to check if it's synchronized
        const elementData = await getTemplatePartsById(elementId);
        if (elementData && elementData.data) {
          const parsedData = JSON.parse(elementData.data || '{}');
          const isSynchronized = !!parsedData[TEMPLATE_SYNC_ELEMENT_IDENTIFIER];

          // Set sync state based on the selected element
          syncState.value = isSynchronized;
          if (parsedData[TEMPLATE_PART_ELEMENT_PART_TYPE] === ElementPartTypeEnum.STYLES) {
            saveStyleOnly.value = true;
          } else {
            saveStyleOnly.value = false;
          }
        }
      }
    };

    const handleValidation = async () => {
      cleanupDynamicStyle();
      const elementData = {
        name: name.value,
        type: props.elementType,
        isSync: forceSync.value ? true : syncState.value,
        renameElement: renameElement.value,
        elementPartType: saveStyleOnly.value ? ElementPartTypeEnum.STYLES : elementPartType.value,
        fromSyncElement: fromSyncElement.value,
        syncParentSyncId: syncParentSyncId.value,
        syncParentId: syncParentId.value,
        selectedElementId: selectedElementId.value, // Add the selected element ID for overwrite
      };

      if (!props.showLoading) {
        context.emit('on-validate', elementData);

        if (props.closeOnValidate) {
          handleClose();
        }
      } else {
        loading.value = true;
        context.emit('on-validate', elementData, (close = true, showMessage = true) => {
          loading.value = false;
          if (showMessage) {
            showToastSuccess(t('templateBuilder.confirm.saveNewElement.success'));
          }
          if (close) {
            handleClose();
          }
        });
      }
    };

    onMounted(() => {
      if (renameElement.value) {
        name.value = renameElement.value.oldName;
      }
    });

    return {
      t,
      v$,
      displayModal,
      headerTitle,
      inputIsSynchronizedDescription,
      loading,
      forceSync,
      renameElement,
      templateRelatedToCampaignBulk,
      isElementSelectedFromSearch,
      canSaveStyleOnly,
      saveStyleOnly,
      handleClose,
      handleValidation,
      handleSearchElements,
      handleClearSelection,
      markAsSelectedFromSearch, // Used for ref
    };
  },
});
